import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cidadeService, empresaService } from '@/services/api';
import { Cidade, Empresa, EmpresaFilters, PaginationResponse } from '@/types';
import { ArrowDownTrayIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const EmpresasPage: React.FC = () => {
  const [filters, setFilters] = useState<EmpresaFilters>({
    page: 1,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [exportando, setExportando] = useState(false);
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchTerm || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateEmpresas } = useQueryInvalidation();

  const [loadError, setLoadError] = useState<string | null>(null);

  const { data: cidadesData } = useQuery({
    queryKey: ['cidades', 'empresas-filtro'],
    queryFn: () => cidadeService.list({ page: 1, limit: 1000 }),
  });

  const cidadesOrdenadas = useMemo(() => {
    const lista = cidadesData?.data ?? [];
    return [...lista].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [cidadesData?.data]);

  const { data: empresasData, isLoading, error } = useQuery<PaginationResponse<Empresa>>({
    queryKey: ['empresas', filters],
    queryFn: () => empresaService.list(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar empresas');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (empresasData) {
      setLoadError(null);
    }
  }, [empresasData]);

  const deleteEmpresaMutation = useMutation({
    mutationFn: empresaService.delete,
    onSuccess: (_data, deletedId) => {
      invalidateEmpresas(deletedId);
      handleSuccess('Empresa excluída com sucesso!');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao excluir empresa');
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleCidadeChange = (cidadeId: number | undefined) => {
    setFilters(prev => ({
      ...prev,
      page: 1,
      cidadeId,
    }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Empresa',
      'Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita.',
      async () => {
        await deleteEmpresaMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  const exportarExcel = async () => {
    const total = empresasData?.pagination.total ?? 0;
    if (total === 0) {
      toast.error('Não há empresas para exportar');
      return;
    }

    try {
      setExportando(true);
      const exportResponse = await empresaService.exportarLista({
        search: filters.search,
        cidadeId: filters.cidadeId,
        maxRows: 2000,
      });

      if (!exportResponse.permissoes.podeExportar) {
        const msg =
          exportResponse.permissoes.mensagem ||
          'Seu plano não permite mais exportações neste período.';
        toast.error(msg);
        return;
      }

      const dadosExportar = exportResponse.dados ?? [];
      if (!dadosExportar.length) {
        toast.error('Não há empresas para exportar');
        return;
      }

      const dadosParaExportar = dadosExportar.map((empresa) => ({
        ID: empresa.id,
        'Razão Social': empresa.nome,
        'Nome Fantasia': empresa.nomeFantasia,
        CNPJ: empresa.cnpj ?? '',
        'CPF Autônomo': empresa.cpfAutonomo ?? '',
        'Nº Classificação Autônomo': empresa.numClassAutonomo ?? '',
        Endereço: empresa.endereco ?? '',
        'Cidade ID': empresa.cidadeId,
        Cidade: empresa.cidade?.nome ?? '',
        UF: empresa.cidade?.uf ?? '',
        'Telefone 1': empresa.telefone1 ?? '',
        'Telefone 2': empresa.telefone2 ?? '',
        Email: empresa.email ?? '',
        Representante: empresa.representante ?? '',
        'Cargo Representante': empresa.cargoRepresentante ?? '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Empresas');

      const maxWidth = 32;
      const colWidths = Object.keys(dadosParaExportar[0] || {}).map(() => ({ wch: maxWidth }));
      worksheet['!cols'] = colWidths;

      const fileName = `empresas-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success('Empresas exportadas com sucesso!');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      const mensagem = e?.response?.data?.error || 'Erro ao exportar empresas';
      console.error('Erro ao exportar empresas:', err);
      toast.error(mensagem);
    } finally {
      setExportando(false);
    }
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Empresas</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie as empresas cadastradas no sistema
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={exportarExcel}
            disabled={exportando || (empresasData?.pagination.total ?? 0) === 0}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {exportando ? 'Exportando...' : 'Exportar Excel'}
          </Button>
          <Link to="/empresas/nova" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto">
              <PlusIcon className="h-4 w-4 mr-2" />
              Nova Empresa
            </Button>
          </Link>
        </div>
      </div>

      {/* Busca e cidade */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div>
              <CardTitle>Filtros</CardTitle>
              <CardDescription>Busque e filtre as empresas cadastradas</CardDescription>
            </div>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:flex-wrap">
              <div className="w-full lg:flex-1 min-w-0">
                <Label htmlFor="empresa-busca" className="text-sm text-gray-600 mb-1 block">
                  Busca
                </Label>
                <div className="relative">
                  <input
                    id="empresa-busca"
                    type="text"
                    placeholder="Nome, fantasia ou CNPJ"
                    className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      aria-label="Limpar busca"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setSearchTerm('')}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <div className="w-full lg:w-72">
                <Label htmlFor="empresa-cidade" className="text-sm text-gray-600 mb-1 block">
                  Cidade
                </Label>
                <select
                  id="empresa-cidade"
                  className="w-full border border-gray-300 pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  value={filters.cidadeId ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleCidadeChange(v === '' ? undefined : Number(v));
                  }}
                >
                  <option value="">Todas as cidades</option>
                  {cidadesOrdenadas.map((c: Cidade) => (
                    <option key={c.id} value={c.id}>
                      {c.nome} / {c.uf}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Empresas List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Empresas</CardTitle>
          <CardDescription>
            {empresasData?.pagination.total || 0} empresas encontradas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : empresasData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhuma empresa encontrada</p>
              <Link to="/empresas/nova">
                <Button className="mt-4">Cadastrar primeira empresa</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {empresasData?.data.map((empresa: Empresa) => (
                <div
                  key={empresa.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">{empresa.nomeFantasia}</h3>
                        <p className="text-sm text-gray-600 truncate">{empresa.nome}</p>
                      </div>
                      <div className="flex space-x-2 flex-shrink-0">
                        <Link to={`/empresas/${empresa.id}/editar`}>
                          <Button variant="outline" size="sm" className="text-yellow-600 hover:text-yellow-700 hover:border-yellow-300" title="Editar">
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(empresa.id)}
                          disabled={deleteEmpresaMutation.isPending}
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                          title="Excluir"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {empresa.cnpj && (
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">CNPJ:</span> {empresa.cnpj}
                        </p>
                      )}
                      {empresa.email && (
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">Email:</span> {empresa.email}
                        </p>
                      )}
                      {empresa.telefone1 && (
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">Telefone:</span> {empresa.telefone1}
                        </p>
                      )}
                      {empresa.cidade && (
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">Cidade:</span> {empresa.cidade.nome}/{empresa.cidade.uf}
                        </p>
                      )}
                      {empresa.representante && (
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">Representante:</span> {empresa.representante}
                        </p>
                      )}
                      {empresa.cargoRepresentante && (
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">Cargo:</span> {empresa.cargoRepresentante}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {empresasData && empresasData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!empresasData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {empresasData.pagination.page} de {empresasData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!empresasData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteEmpresaMutation.isPending}
      />
    </div>
  );
};
